/**
 * TikTok API Service
 *
 * Wraps TikTok integration endpoints:
 * - Connection status check
 * - Video publishing (via backend 2-step flow)
 * - Media upload to Supabase Storage (for getting public URL)
 */

import { apiRequest, getAuthToken, getWorkspaceId, API_BASE_URL, refreshSession } from './core.js';
import { logger } from '../../lib/logger.js';
import { buildMediaUploadFormData, materializeUploadFile } from './buildMediaUploadFormData.js';

// ==================== Types ====================

interface TikTokStatusResponse {
  success: boolean;
  data: {
    connected: boolean;
    openId?: string;
    expiresAt?: string;
  };
}

interface TikTokCreatorInfoResponse {
  success: boolean;
  data: {
    creatorAvatarUrl: string;
    creatorNickname: string;
    privacyLevelOptions: string[];
    maxVideoPostDurationSec: number;
    commentDisabled: boolean;
    duetDisabled: boolean;
    stitchDisabled: boolean;
  };
}

/**
 * Statuses returned by TikTok's publish/status/fetch endpoint, mirrored
 * from server/src/services/connectors/tiktokConnector.ts. Distinct from
 * the local UI state machine in SocialAutomator/types.ts (TikTokPublishStatus
 * = 'idle' | 'uploading' | …) — that one tracks our client-side polling
 * lifecycle, this one is the raw TikTok API value.
 */
export type TikTokApiPublishStatus =
  | 'PROCESSING_DOWNLOAD'
  | 'PROCESSING_UPLOAD'
  | 'SEND_TO_USER_INBOX'
  | 'PUBLISH_COMPLETE'
  | 'FAILED'
  | 'UNKNOWN';

interface TikTokPublishStatusResponse {
  success: boolean;
  data: {
    status: TikTokApiPublishStatus;
    failReason?: string;
  };
}

interface TikTokPublishRequest {
  videoUrl?: string;
  /** Photo / carousel slides — uses TikTok PHOTO Direct Post. */
  photoUrls?: string[];
  caption: string;
  privacyLevel: string;
  disableComment: boolean;
  disableDuet: boolean;
  disableStitch: boolean;
  brandContentToggle: boolean;
  brandOrganicToggle: boolean;
  musicConfirmed: true;
  brandedPolicyConfirmed: boolean;
  videoDurationSec?: number;
  isAigc?: boolean;
}

interface TikTokUploadDraftRequest {
  videoUrl: string;
  caption: string;
}

interface TikTokPublishResponse {
  success: boolean;
  data: {
    publishId: string;
  };
}

interface MediaUploadResponse {
  success: boolean;
  data: {
    id: string;
    url: string;
    storagePath: string;
    filename: string;
    mimeType: string;
    size: number;
    type: string;
  };
}

// ==================== API Functions ====================

/**
 * Check if TikTok is connected for the current workspace
 */
function checkStatus(): Promise<TikTokStatusResponse> {
  return apiRequest<TikTokStatusResponse>('/tiktok/status');
}

/**
 * Publish a video to TikTok
 * Backend handles 2-step flow: init upload → poll status (up to 5 min)
 */
function publish(options: TikTokPublishRequest): Promise<TikTokPublishResponse> {
  return apiRequest<TikTokPublishResponse>('/tiktok/publish', {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

/** Cap so a stuck multer/empty-body wait fails closed instead of "Scheduling?". */
const MEDIA_UPLOAD_TIMEOUT_MS = 120_000;

/**
 * Upload a media file to Supabase Storage via backend.
 * Uses raw fetch (not apiRequest) because multipart/form-data
 * needs the browser to set the Content-Type boundary automatically.
 *
 * IMPORTANT: materialize bytes + build a fresh FormData for every attempt.
 * Reusing FormData after 401, or appending a detached/empty picker File,
 * leaves multer waiting forever (DevTools: POST /media/upload Pending).
 */
async function uploadMedia(file: File): Promise<MediaUploadResponse> {
  const uploadFile = await materializeUploadFile(file);

  const doFetch = async (): Promise<Response> => {
    const formData = buildMediaUploadFormData(uploadFile);
    const token = getAuthToken();
    const workspaceId = getWorkspaceId();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (workspaceId) headers['X-Workspace-Id'] = workspaceId;
    // Do not set Content-Type ? fetch must add multipart boundary.
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), MEDIA_UPLOAD_TIMEOUT_MS);
    try {
      return await fetch(`${API_BASE_URL}/media/upload`, {
        method: 'POST',
        headers,
        body: formData,
        signal: controller.signal,
      });
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new Error('Media upload timed out. Check your connection and try again.');
      }
      throw err;
    } finally {
      window.clearTimeout(timer);
    }
  };

  const started = performance.now();
  logger.debug('[uploadMedia] start', {
    name: uploadFile.name,
    type: uploadFile.type,
    bytes: uploadFile.size,
  });

  let response = await doFetch();
  if (response.status === 401) {
    logger.debug('[uploadMedia] 401 ? refreshing session and retrying with new FormData');
    const refreshed = await refreshSession();
    if (refreshed) response = await doFetch();
  }

  if (!response.ok) {
    const errorBody = (await response.json().catch(() => ({}))) as {
      error?: { message?: string } | string;
      message?: string;
    };
    const nested = errorBody.error;
    const nestedMsg =
      typeof nested === 'object' && nested !== null ? nested.message : undefined;
    throw new Error(
      nestedMsg || errorBody.message || `Failed to upload media (${response.status})`,
    );
  }

  const json = (await response.json()) as MediaUploadResponse;
  if (!json.data?.url) {
    throw new Error('Upload succeeded but no media URL was returned.');
  }
  logger.debug('[uploadMedia] ok', {
    ms: Math.round(performance.now() - started),
    status: response.status,
    bytes: uploadFile.size,
    name: uploadFile.name,
    urlHost: (() => {
      try {
        return new URL(json.data.url).host;
      } catch {
        return '(invalid)';
      }
    })(),
  });
  return json;
}

/**
 * Upload a video to the creator's TikTok drafts (inbox).
 * Uses the video.upload scope — simpler than Direct Post (no privacy/commercial settings).
 */
function uploadToDraft(options: TikTokUploadDraftRequest): Promise<TikTokPublishResponse> {
  return apiRequest<TikTokPublishResponse>('/tiktok/upload-draft', {
    method: 'POST',
    body: JSON.stringify(options),
  });
}

/**
 * Get creator info for TikTok posting UI compliance
 */
function getCreatorInfo(): Promise<TikTokCreatorInfoResponse> {
  return apiRequest<TikTokCreatorInfoResponse>('/tiktok/creator-info');
}

/**
 * Get publish status for a single publish ID (for polling)
 */
function getPublishStatus(publishId: string): Promise<TikTokPublishStatusResponse> {
  return apiRequest<TikTokPublishStatusResponse>(`/tiktok/publish-status/${publishId}`);
}

export const tiktokApi = {
  checkStatus,
  publish,
  uploadToDraft,
  uploadMedia,
  getCreatorInfo,
  getPublishStatus,
};
